// @ts-nocheck
import adjustableScrollToId from "@store/Framework/Common/Utilities/adjustableScrollToId";
import useHeaderHeight from "@store/Framework/Store/Bunzl/Components/Header/Hooks/useHeaderHeight";
import useGetFirstInputInError from "@store/Framework/Store/Foundation/Components/Form/FormHooks/useGetFirstInputInError";
import useGetInputs from "@store/Framework/Store/Foundation/Components/Form/FormHooks/useGetInputs";
import useHasErrorInForm from "@store/Framework/Store/Foundation/Components/Form/FormHooks/useHasErrorInForm";
import useInitForm from "@store/Framework/Store/Foundation/Components/Form/FormHooks/useInitForm";
import setHasAlreadyErrors from "@store/Framework/Store/Foundation/Components/Form/Handlers/SetHasAlreadyErrors";
// import StoreFormContextProvider from "@store/Library/Components/Foundation/StoreForm/StoreFormContextProvider"; // Store Custom Code
// import InfosBox from "@store/Library/Components/Toolkit/InfosBox"; // Store Custom Code
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Link from "@insite/mobius/Link";
import { LinkPresentationProps } from "@insite/mobius/Link/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { createRef, FC, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

/* Store Custom Code - Start */
import StoreFormContextProvider from "@store/Library/Components/Foundation/StoreForm/StoreFormContextProvider";
import InfosBox from "@store/Library/Components/Toolkit/InfosBox";
/* Store Custom Code - End */

type Props = {
    generalErrorMessage?: string;
    disableAutoFocus?: boolean;
    hideErrorsBox?: boolean;
    extendedStyles?: StoreFormWrapperStyles;
};

export interface StoreFormWrapperStyles {
    wrapper?: InjectableCss;
    errorsListTitle?: TypographyPresentationProps;
    errorsList?: TypographyPresentationProps;
    errorsListItem?: TypographyPresentationProps;
    errorsLink?: LinkPresentationProps;
}

export const storeFormWrapperStyles: StoreFormWrapperStyles = {
    errorsListTitle: {
        color: "danger.main",
        css: css`
            display: block;
            margin-bottom: 0.25em;
            font-weight: ${({ theme }) => theme.design.typography.weight.bold};
        `,
    },
    errorsLink: {
        color: "text.main",
        css: css`
            font-weight: ${({ theme }) => theme.design.typography.weight.bold};

            &:focus {
                outline: ${pxToRem(5)} auto -webkit-focus-ring-color;
            }
        `,
        typographyProps: {
            underline: true,
        },
    },
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
};

const StoreFormWrapper: FC<Props> = ({ extendedStyles, generalErrorMessage, children }) => {
    const { form } = useInitForm();
    const dispatch = useDispatch();
    const formId = form.id;
    const [styles] = useState(() => mergeToNew(storeFormWrapperStyles, extendedStyles));
    const inputs = useGetInputs(formId);
    const firstInputInError = useGetFirstInputInError(formId);
    const hasError = useHasErrorInForm(formId);
    const wrapperRef = createRef<HTMLDivElement>();
    const formWrapperRef = createRef<any>(); // Kishigo
    const { headerHeight } = useHeaderHeight(); // Kishigo
    const scrollPadding = 50; // Kishigo
    const formWrapperInfosBoxId = "form-wrapper-infos-box"; // Kishigo

    useEffect(() => {
        if (hasError) {
            if (!form.hasAlreadyError) {
                anchorLinkHandler(`${firstInputInError?.id}-link`);
            }
        }
        dispatch(setHasAlreadyErrors({ formId }));
    }, [hasError]);

    useEffect(() => {
        if (generalErrorMessage) {
            anchorLinkHandler(formWrapperInfosBoxId);
        }
    }, [generalErrorMessage]);

    const anchorLinkHandler = useCallback(
        (id: string) => {
            adjustableScrollToId(id, headerHeight.mainHeight + scrollPadding, true);
        },
        [headerHeight],
    );

    const errorContent = () => (
        <div ref={wrapperRef}>
            <Typography {...styles.errorsListTitle}>{translate("Some errors have to be resolved:")}</Typography>
            <Typography as="ul" {...styles.errorsList}>
                {inputs.map(({ error, id }) => {
                    if (error) {
                        return (
                            <Typography key={id} as="li" role="alert" {...styles.errorsListItem}>
                                <Link id={`${id}-link`} onClick={() => anchorLinkHandler(id)} {...styles.errorsLink}>
                                    {error}
                                </Link>
                            </Typography>
                        );
                    }
                    return null;
                })}
            </Typography>
        </div>
    );

    return (
        <StoreFormContextProvider form={form}>
            <StyledWrapper {...styles.wrapper} ref={formWrapperRef}>
                {hasError || generalErrorMessage ? (
                    <InfosBox type="error" id={formWrapperInfosBoxId}>
                        {generalErrorMessage || errorContent()}
                    </InfosBox>
                ) : null}
                {children}
            </StyledWrapper>
        </StoreFormContextProvider>
    );
};

export default StoreFormWrapper;
